import React from 'react';
import Reveal from '../motion/Reveal';
import './ServicesSection.css';

const ServicesSection = () => {
  // Service schema reflecting the v3 reference stack
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Full-stack AI Product Development",
    "provider": {
      "@type": "Organization",
      "name": "PySolutions",
      "url": "https://pysolutions.dev"
    },
    "serviceType": "AI Product Engineering",
    "areaServed": "Worldwide",
    "description": "Senior-led full-stack engineering for production AI products: backend, frontend, AI, cloud, and DevOps shipped end to end.",
    "category": "Information Technology",
    "audience": {
      "@type": "Audience",
      "audienceType": "Business"
    }
  };

  const services = [
    {
      id: 1,
      slug: "ai-agents",
      title: "AI Agents & RAG",
      tagline: "Grounded answers and agentic workflows that hold up past the demo, on a stack we already run in production.",
      technologies: ["LangGraph", "MCP servers", "Postgres + pgvector"],
      color: "blue"
    },
    {
      id: 2,
      slug: "full-stack-ai",
      title: "Full-stack AI Products",
      tagline: "From empty repo to a shipped AI product, on the stack we already run at scale.",
      technologies: ["Next.js 16", "FastAPI", "Claude Code-accelerated"],
      color: "orange"
    },
    {
      id: 3,
      slug: "llm-fine-tuning",
      title: "LLM Fine-tuning & Eval",
      tagline: "Domain adapters and eval harnesses that move accuracy on your data, not on someone else's benchmark.",
      technologies: ["LoRA / QLoRA", "vLLM", "Braintrust evals"],
      color: "purple"
    },
    {
      id: 4,
      slug: "python-backends",
      title: "Python Backends & Platform",
      tagline: "API platforms, async workers, and the operational backbone behind every AI product we ship.",
      technologies: ["FastAPI", "Django", "async SQLAlchemy 2.0"],
      color: "teal"
    },
    {
      id: 5,
      slug: "cloud-and-devops",
      title: "Cloud & DevOps",
      tagline: "Cloud architecture, CI/CD, and the operations layer that keeps shipped products running cleanly.",
      technologies: ["AWS / GCP / Azure", "Docker / Kubernetes", "GitHub Actions / Terraform"],
      color: "green"
    },
    {
      id: 6,
      slug: "data-iot",
      title: "Data & IoT Pipelines",
      tagline: "Streaming pipelines and edge ML for teams that already have the data and need it to move.",
      technologies: ["Kafka / Redis Streams", "NVIDIA Jetson", "OpenTelemetry"],
      color: "red"
    }
  ];

  return (
    <section id="services" className="services-section section">
      {/* Service Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      <div className="services-container">
        <div className="section-header">
          <div className="section-badge">
            <span className="badge-icon">▸</span>
            What we ship
          </div>
          <h2 className="section-title">
            Full coverage, end <span className="logo-highlight">to end</span>
          </h2>
          <p className="section-subtitle">
            Backend, frontend, AI, cloud, and DevOps — the surfaces a real product engagement actually needs. Built AI-accelerated with Claude Code and the latest frontier models, reviewed by senior engineers, and shipped.
          </p>
        </div>

        <div className="services-grid">
          {services.map((service, index) => (
            <Reveal key={service.id} className="service-card-reveal" delay={(index % 3) * 0.08}>
            <div id={service.slug} className={`service-card service-${service.color}`}>
              <div className="service-card-inner">
                <div className="service-header">
                  <div className="service-icon">
                    <img
                      src={`/media/icons/${service.slug}.webp`}
                      alt=""
                      width="72"
                      height="72"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                </div>

                <p className="service-description">{service.tagline}</p>

                <div className="service-technologies">
                  <span className="tech-label">Reference stack:</span>
                  <div className="tech-tags">
                    {service.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <a href="mailto:info@pysolutions.dev" className="service-learn-more" aria-label={`Discuss a ${service.title} engagement`}>
                  Discuss an engagement
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                  </svg>
                </a>
              </div>

              <div className="service-overlay"></div>
            </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
